import {Injectable} from '@angular/core';
import {MultiselectProvider} from '../multiselect-provider';
import {Observable, Observer} from 'rxjs';
import {MultiselectOptionItem, UiConstants} from '../../util/core-utils';
import {OrderType, ResourceQueryResult, Services} from '../util/services';
import {Set} from 'immutable';
import {Device, DeviceManagement, DeviceManagementService, DeviceQuery} from "../device-management.service";

@Injectable()
export class DeviceMultiselectProvider implements MultiselectProvider {

  private multiselectQueryRequest: DeviceQuery = {
    page_number: 1,
    number_of_items: UiConstants.autocompletePageSize,
    no_progress_bar: true,
    order: Services.createOrderFieldParameter(DeviceManagement.Keys.toOrderFieldKey,
      Set.of(
        {field: DeviceManagement.OrderField.NAME, type: OrderType.ASC},
        {field: DeviceManagement.OrderField.APPLICATION_ID, type: OrderType.ASC})),
    disabled: undefined,
    fields: ['id', 'name', 'application_id', 'disabled'].join(','),
    q: undefined
  };

  constructor(private deviceManagementService: DeviceManagementService) {
  }

  load(queryRequest: DeviceQuery): Observable<MultiselectOptionItem<number>[]> {
    return Observable.create((observer: Observer<MultiselectOptionItem<number>[]>) => {
      this.deviceManagementService.query(queryRequest).subscribe((result: ResourceQueryResult<Device>) => {
          observer.next(result.items.map(item => this.toMultiselectOptionItem(item!)));
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  loadAll(predicate?: string): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign({}, this.multiselectQueryRequest);
    queryRequest.q = predicate;
    return this.load(queryRequest);
  }

  loadActive(predicate?: string): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign({}, this.multiselectQueryRequest);
    queryRequest.q = predicate;
    queryRequest.disabled = false;
    return this.load(queryRequest);
  }

  loadActiveWithIdSet(id: Set<number>, predicate?: string): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign({}, this.multiselectQueryRequest);
    queryRequest.q = predicate;
    queryRequest.id = Services.createIdParameter(id);
    queryRequest.disabled = false;
    return this.load(queryRequest);
  }

  getById(id: number): Observable<MultiselectOptionItem<number>> {
    return Observable.create((observer: Observer<MultiselectOptionItem<number>>) => {
      this.deviceManagementService.get({
        id: id
      }).subscribe((result: Device) => {
          observer.next(this.toMultiselectOptionItem(result!));
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  getByIds(ids: number[]): Observable<MultiselectOptionItem<number>[]> {
    const queryRequest = Object.assign([], this.multiselectQueryRequest);
    queryRequest.id = ids.join(',');
    queryRequest.number_of_items = undefined;
    queryRequest.page_number = undefined;
    return this.load(queryRequest);
  }

  toMultiselectOptionItem(item: Device): MultiselectOptionItem<number> {
    const ret = new MultiselectOptionItem<number>();
    ret.id = item.id;
    ret.itemName = item.name ? item.name + ' (' + item.application_id + ')' : item.application_id
    ret.disabled = item.disabled;
    return ret;
  }

}
